// Mes Candidatures - Frontend connecté au Backend
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Building2, Clock, CheckCircle, XCircle, Calendar, 
  MessageCircle, Eye, ChevronRight, Loader2, X, MapPin, Euro,
  ExternalLink, Trash2
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Modal pour voir le détail de l'offre
function OffreDetailModal({ isOpen, onClose, candidature }) {
  if (!isOpen || !candidature) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 md:left-[288px] bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl w-full max-w-lg shadow-xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Détail de l'offre</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Building2 size={28} className="text-gray-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{candidature.job_title}</h3>
                <p className="text-cyan-600 font-medium">{candidature.company}</p>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin size={16} />
                <span>{candidature.location}</span>
              </div>
              {candidature.salary && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Euro size={16} />
                  <span>{candidature.salary}</span>
                </div>
              )}
            </div>

            {candidature.message && (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-medium text-gray-500 mb-1">Ton message de candidature</p>
                <p className="text-sm text-gray-700">{candidature.message}</p>
              </div>
            )}

            <div className="pt-2">
              <Button variant="outline" className="w-full" onClick={onClose}>
                Fermer
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Modal de contact
function ContactModal({ isOpen, onClose, candidature }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Écris un message');
      return;
    }
    setSending(true);
    // Simulate sending
    await new Promise(r => setTimeout(r, 1000));
    toast.success('Message envoyé');
    setSending(false);
    setMessage('');
    onClose();
  };

  if (!isOpen || !candidature) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 md:left-[288px] bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl w-full max-w-md shadow-xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Contacter {candidature.company}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="Bonjour, je souhaiterais avoir plus d'informations..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={onClose} disabled={sending}>
                Annuler
              </Button>
              <Button className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white" onClick={handleSend} disabled={sending}>
                {sending ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                Envoyer
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function CandidatureCard({ candidature, onViewOffre, onContact, onDelete }) {
  const statusConfig = {
    pending: { label: 'En attente', bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
    viewed: { label: 'Vue', bg: 'bg-blue-100', text: 'text-blue-700', icon: Eye },
    interview: { label: 'Entretien', bg: 'bg-green-100', text: 'text-green-700', icon: Calendar },
    accepted: { label: 'Acceptée', bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
    rejected: { label: 'Refusée', bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
  };
  const status = statusConfig[candidature.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 p-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <Building2 size={22} className="text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{candidature.job_title}</h3>
              <p className="text-xs text-cyan-600 font-medium">{candidature.company}</p>
            </div>
            <span className={`flex-shrink-0 px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 ${status.bg} ${status.text}`}>
              <StatusIcon size={10} />
              {status.label}
            </span>
          </div>
          
          <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <MapPin size={10} />
              {candidature.location}
            </span>
            {candidature.salary && (
              <span className="flex items-center gap-1">
                <Euro size={10} />
                {candidature.salary}
              </span>
            )}
          </div>

          <p className="text-xs text-gray-400 mt-1.5">Candidature envoyée le {candidature.date}</p>

          {candidature.status === 'interview' && candidature.interview_date && (
            <div className="mt-2 p-2 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700">
                <span className="font-semibold">Entretien prévu :</span> {candidature.interview_date}
              </p>
            </div>
          )}

          {candidature.status === 'rejected' && candidature.feedback && (
            <div className="mt-2 p-2 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600">{candidature.feedback}</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
        <Button variant="outline" size="sm" className="flex-1 text-xs h-8" onClick={() => onViewOffre(candidature)}>
          <Eye size={12} className="mr-1" /> Voir l'offre
        </Button>
        <Button variant="outline" size="sm" className="flex-1 text-xs h-8" onClick={() => onContact(candidature)}>
          <MessageCircle size={12} className="mr-1" /> Contacter
        </Button>
        <Button variant="outline" size="sm" className="text-red-500 hover:bg-red-50 h-8 px-2" onClick={() => onDelete(candidature.id)}>
          <Trash2 size={12} />
        </Button>
      </div>
    </motion.div>
  );
}

export default function MesCandidatures() {
  const { user } = useCurrentUser();
  const [filter, setFilter] = useState('all');
  const [candidatures, setCandidatures] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, viewed: 0, interview: 0, accepted: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedCandidature, setSelectedCandidature] = useState(null);
  const [showOffreModal, setShowOffreModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  
  // Refs pour le scroll des filtres mobile
  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Charger les candidatures
  useEffect(() => {
    // Toujours utiliser demo-user pour la démo
    const userId = user?.id || 'demo-user';
    loadCandidatures(userId);
    loadStats(userId);
  }, [user?.id]);

  const loadCandidatures = async (userId) => {
    try {
      setLoading(true);
      const id = userId || 'demo-user';
      const res = await fetch(`${API_URL}/api/candidatures/user/${id}`);
      if (res.ok) {
        const data = await res.json();
        // Si pas de candidatures pour cet utilisateur, charger demo-user
        if (data.length === 0 && id !== 'demo-user') {
          const demoRes = await fetch(`${API_URL}/api/candidatures/user/demo-user`);
          if (demoRes.ok) {
            const demoData = await demoRes.json();
            setCandidatures(demoData);
            return;
          }
        }
        setCandidatures(data);
      }
    } catch (err) {
      console.error('Erreur chargement candidatures:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (userId) => {
    try {
      const id = userId || 'demo-user';
      const res = await fetch(`${API_URL}/api/candidatures/user/${id}/stats`);
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Erreur chargement stats:', err);
    }
  };

  const handleViewOffre = (candidature) => {
    setSelectedCandidature(candidature);
    setShowOffreModal(true);
  };

  const handleContact = (candidature) => {
    setSelectedCandidature(candidature);
    setShowContactModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Retirer cette candidature ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/candidatures/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setCandidatures(prev => prev.filter(c => c.id !== id));
        loadStats();
        toast.success('Candidature retirée');
      }
    } catch (err) {
      console.error('Erreur suppression:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  const filters = [
    { id: 'all', label: 'Toutes', icon: null },
    { id: 'pending', label: 'En attente', icon: Clock },
    { id: 'viewed', label: 'Vues', icon: Eye },
    { id: 'interview', label: 'Entretiens', icon: Calendar },
  ];

  const filteredCandidatures = candidatures.filter(c => filter === 'all' || c.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={32} className="animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div>
        <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mes Candidatures</h1>
        <p className="text-sm text-gray-500">{stats.total} candidature{stats.total > 1 ? 's' : ''}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'En attente', value: stats.pending, color: 'text-yellow-600' },
          { label: 'Vues', value: stats.viewed, color: 'text-blue-600' },
          { label: 'Entretiens', value: stats.interview, color: 'text-green-600' },
          { label: 'Acceptées', value: stats.accepted, color: 'text-emerald-600' },
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-xl p-3 border border-gray-200 text-center">
            <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
            <p className="text-xs text-gray-500">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 p-2">
        {/* Mobile */}
        <div className="sm:hidden">
          <div className="relative flex items-center">
            <div 
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {filters.map((f) => {
                const count = f.id === 'all' ? stats.total : stats[f.id] || 0;
                const FilterIcon = f.icon;
                return (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                      filter === f.id
                        ? 'bg-orange-500 text-white shadow-sm'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    {FilterIcon && <FilterIcon size={12} />}
                    {f.label}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      filter === f.id ? 'bg-white/20' : 'bg-gray-100'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
            {showScrollHint && (
              <div className="flex-shrink-0 flex items-center pointer-events-none">
                <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
              </div>
            )}
          </div>
        </div>

        {/* Desktop */}
        <div className="hidden sm:flex flex-wrap gap-2">
          {filters.map((f) => {
            const count = f.id === 'all' ? stats.total : stats[f.id] || 0;
            const FilterIcon = f.icon;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-2 ${
                  filter === f.id
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {FilterIcon && <FilterIcon size={12} />}
                {f.label}
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  filter === f.id ? 'bg-white/20' : 'bg-gray-200'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Liste */}
      {filteredCandidatures.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-sm font-semibold text-gray-900 mb-2">
            {filter === 'all' ? 'Aucune candidature' : `Aucune candidature ${filters.find(f => f.id === filter)?.label.toLowerCase()}`}
          </h3>
          <p className="text-xs text-gray-500">
            {filter === 'all' 
              ? 'Tu n\'as pas encore postulé à une offre' 
              : 'Change de filtre pour voir tes autres candidatures'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredCandidatures.map(candidature => (
            <CandidatureCard 
              key={candidature.id} 
              candidature={candidature}
              onViewOffre={handleViewOffre}
              onContact={handleContact}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <OffreDetailModal
        isOpen={showOffreModal}
        onClose={() => setShowOffreModal(false)}
        candidature={selectedCandidature}
      />
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        candidature={selectedCandidature}
      />
    </div>
  );
}
